// FindNaverRankTracking.jsx
import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { HotTable } from '@handsontable/react-wrapper';
import 'handsontable/styles/handsontable.css';
import 'handsontable/styles/ht-theme-main.css';
import './FindNaverRankTracking.css';
import AuthService from '../../services/authService';
import { dispatchQuotaUpdated } from '../../utils/quotaEvent';
import ProtectedMenuPage from '../../components/ProtectedMenuPage/ProtectedMenuPage';
import KakaoFloatButton from '../../components/KakaoFloatButton/KakaoFloatButton';
import {
    Card,
    Row,
    Col,
    Button,
    Typography,
    Space,
    Spin,
    message,
    Popover,
    Switch,
    Modal,
} from 'antd';
import { SearchOutlined, DownloadOutlined, ReloadOutlined, EyeOutlined } from '@ant-design/icons';
const COL_WIDTHS_STORAGE_KEY = 'findNaverRankTracking_colWidths';
const COL_VISIBILITY_STORAGE_KEY = 'findNaverRankTracking_colVisibility';
const DEFAULT_COL_WIDTHS = [100, 100, 100, 120, 180, 200, 150, 120, 120];
const DEFAULT_DATE_COL_WIDTH = 145;

const loadSavedColWidths = () => {
    try {
        const saved = localStorage.getItem(COL_WIDTHS_STORAGE_KEY);
        if (!saved) return null;
        const arr = JSON.parse(saved);
        return Array.isArray(arr) && arr.length > 0 ? arr : null;
    } catch {
        return null;
    }
};

const saveColWidths = widths => {
    try {
        localStorage.setItem(COL_WIDTHS_STORAGE_KEY, JSON.stringify(widths));
    } catch (e) {
        console.warn('colWidths 저장 실패:', e);
    }
};

const COL_VISIBILITY_OPTIONS = [
    { key: 'memo1', label: '메모1' },
    { key: 'memo2', label: '메모2' },
    { key: 'memo3', label: '메모3' },
    { key: 'store', label: '스토어명' },
    { key: 'product', label: '상품명' },
    { key: 'productUrl', label: '상품 URL' },
];

const loadColVisibility = () => {
    try {
        const saved = localStorage.getItem(COL_VISIBILITY_STORAGE_KEY);
        if (!saved) return null;
        const obj = JSON.parse(saved);
        const def = {
            memo1: true,
            memo2: true,
            memo3: true,
            store: true,
            product: true,
            productUrl: true,
        };
        return { ...def, ...obj };
    } catch {
        return null;
    }
};

const saveColVisibility = obj => {
    try {
        localStorage.setItem(COL_VISIBILITY_STORAGE_KEY, JSON.stringify(obj));
    } catch (e) {
        console.warn('colVisibility 저장 실패:', e);
    }
};

/** 셀 표시값에서 숫자 순위만 추출 (예: "3(▲2)" → 3) */
const parseRankNumber = val => {
    if (val == null) return null;
    const n = parseInt(val, 10);
    return Number.isNaN(n) ? null : n;
};

/** 전날 순위와 비교해 등락 포맷 (상승: ▲ 빨강, 하락: ▼ 파랑) */
const formatRankWithChange = (currentRank, prevRank) => {
    if (
        currentRank == null ||
        prevRank == null ||
        currentRank === 'norank' ||
        prevRank === 'norank'
    )
        return null;
    const curr = typeof currentRank === 'number' ? currentRank : parseRankNumber(currentRank);
    const prev = typeof prevRank === 'number' ? prevRank : parseRankNumber(prevRank);
    if (curr == null || prev == null) return null;
    const diff = prev - curr; // 양수 = 순위 상승(개선), 음수 = 순위 하락
    if (diff > 0) return `${curr}(▲${diff})`;
    if (diff < 0) return `${curr}(▼${Math.abs(diff)})`;
    return null; // 변화 없음
};

/** 순위 셀 렌더러 - (▲n)/(▼n) 작게 표시, 색상·중앙정렬 적용 */
const rankCellRenderer = (instance, td, row, col, prop, value) => {
    td.innerHTML = '';
    td.style.textAlign = 'center';
    if (value == null || value === '') return td;
    if (typeof value === 'string' && value.includes('loading-spinner')) {
        td.innerHTML = value;
        td.classList.add('processing-row');
        return td;
    }
    const str = String(value);
    const isUp = str.includes('▲');
    const isDown = str.includes('▼');
    if (isUp) {
        td.style.color = '#dc2626';
        td.style.fontWeight = '600';
    } else if (isDown) {
        td.style.color = '#2563eb';
        td.style.fontWeight = '600';
    } else {
        td.style.color = '';
        td.style.fontWeight = '';
    }
    const match = str.match(/^(\d+)(\([▲▼]\d+\))$/);
    if (match) {
        td.innerHTML = `<span>${match[1]}</span><span style="font-weight:500;margin-left:2px;">${match[2]}</span>`;
    } else {
        td.textContent = str;
    }
    return td;
};

const FindNaverRankTracking = () => {
    const { Text } = Typography;
    const [loading, setLoading] = useState(false);
    const [isSearchInProgress, setIsSearchInProgress] = useState(false);
    const [isRefreshLoading, setIsRefreshLoading] = useState(false);
    const [maxRows, setMaxRows] = useState(20);
    const [dbRowCount, setDbRowCount] = useState(0);
    const [loadingMessage, setLoadingMessage] = useState('');
    const cutBufferRef = useRef([]);
    const hotRef = useRef(null);
    /** Modal 확인 후 alter로 행 삭제 시 beforeRemoveRow가 다시 뜨지 않도록 */
    const skipRemoveRowConfirmRef = useRef(false);
    const rightClickedRowRef = useRef(null);
    const rightClickedColRef = useRef(null);
    const [tableData, setTableData] = useState([]);
    const pollingRef = useRef(null);
    const trackingMidSearchLockRef = useRef(false);
    const stripHtml = s => (typeof s === 'string' ? s.replace(/<[^>]+>/g, '') : s);
    const [colVisibility, setColVisibility] = useState(() => {
        const loaded = loadColVisibility();
        return (
            loaded ?? {
                memo1: true,
                memo2: true,
                memo3: true,
                store: true,
                product: true,
                productUrl: true,
            }
        );
    });

    useEffect(() => {
        saveColVisibility(colVisibility);
    }, [colVisibility]);

    const handleExportExcel = () => {
        try {
            const hot = hotRef.current?.hotInstance;
            const headers = (visibleColHeaders || []).map(h => {
                const visible = h && h.includes('|') ? h.split('|')[0] : h;
                return stripHtml(visible || '');
            });
            // 화면에 보이는 최신 데이터 사용
            const data = hot ? hot.getData() : tableData;

            // 전체 내보내기 (빈 행 포함) → 필요시 아래 필터로 빈행 제거 가능
            // const nonEmptyData = data.filter(row => row.some(cell => (cell ?? '').toString().trim() !== ''));

            const aoa = [headers, ...data];
            const ws = XLSX.utils.aoa_to_sheet(aoa);

            // 컬럼 너비 약간 가독성 있게
            ws['!cols'] = headers.map(h => ({ wch: Math.max(10, String(h).length + 2) }));

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'RankTracking');

            const now = new Date();
            const pad = n => String(n).padStart(2, '0');
            const filename = `rank_tracking_${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}.xlsx`;
            XLSX.writeFile(wb, filename);
        } catch (e) {
            console.error('엑셀 내보내기 실패:', e);
            message.error('엑셀 내보내기 중 오류가 발생했습니다.');
        }
    };

    const BASE_COL_HEADERS = [
        '메모1',
        '메모2',
        '메모3',
        '스토어명',
        '상품명',
        '상품 URL',
        '순위 조회 키워드 <span class="text-danger">*</span>',
        '단일 MID <span class="text-danger">*</span>',
        '원부 MID',
    ];
    const BASE_COLUMNS = [
        { data: 0 },
        { data: 1 },
        { data: 2 },
        { data: 3 },
        { data: 4 },
        { data: 5 },
        { data: 6 },
        { data: 7 },
        { data: 8 },
    ];

    const [colHeaders, setColHeaders] = useState([...BASE_COL_HEADERS]);

    const [columns, setColumns] = useState([
        { data: 0 },
        { data: 1 },
        { data: 2 },
        { data: 3 },
        { data: 4 },
        { data: 5 },
        { data: 6 },
        { data: 7 },
        { data: 8 },
    ]);

    const [colWidths, setColWidths] = useState(() => {
        const saved = loadSavedColWidths();
        if (saved && saved.length >= 9) return saved.slice(0, 9);
        return [...DEFAULT_COL_WIDTHS];
    });

    const isBaseColVisible = useCallback(
        i => {
            if (i === 0) return colVisibility.memo1;
            if (i === 1) return colVisibility.memo2;
            if (i === 2) return colVisibility.memo3;
            if (i === 3) return colVisibility.store;
            if (i === 4) return colVisibility.product;
            if (i === 5) return colVisibility.productUrl;
            return true;
        },
        [colVisibility]
    );

    const visibleColHeaders = useMemo(() => {
        const base = colHeaders.slice(0, 9);
        const rank = colHeaders.slice(9);
        const visibleBase = [0, 1, 2, 3, 4, 5].filter(i => isBaseColVisible(i)).map(i => base[i]);
        const fixedBase = base.slice(6, 9);
        return [...visibleBase, ...fixedBase, ...rank];
    }, [colHeaders, isBaseColVisible]);

    const visibleColumns = useMemo(() => {
        const base = columns.slice(0, 9);
        const rank = columns.slice(9);
        const visibleBase = [0, 1, 2, 3, 4, 5].filter(i => isBaseColVisible(i)).map(i => base[i]);
        const fixedBase = base.slice(6, 9);
        return [...visibleBase, ...fixedBase, ...rank];
    }, [columns, isBaseColVisible]);

    const visibleColWidths = useMemo(() => {
        const base = colWidths.slice(0, 9);
        const rank = colWidths.slice(9);
        const visibleBase = [0, 1, 2, 3, 4, 5]
            .filter(i => isBaseColVisible(i))
            .map(i => base[i] ?? DEFAULT_COL_WIDTHS[i]);
        const fixedBase = base.slice(6, 9);
        return [...visibleBase, ...fixedBase, ...rank];
    }, [colWidths, isBaseColVisible]);

    const fetchInitialData = async () => {
        try {
            setLoading(true);
            setLoadingMessage('데이터를 불러오는 중입니다...');
            const [limitRes, rowsRes] = await Promise.all([
                AuthService.authAxios.get('/settings/rank-limit', {
                    params: { gubun: 'findNaverRankTracking' },
                }),
                AuthService.authAxios.get('/product/rank/tracking/rows'),
            ]);

            const cap = limitRes?.data?.success ? limitRes.data.max_rows : 20;
            setMaxRows(cap);

            const response = { data: rowsRes.data };
            if (response.data.success) {
                const rows = [];
                const data = response.data.data;
                setDbRowCount(data.length);
                if (data.length > 0) {
                    // 데이터를 sort_order 기준으로 정렬
                    const sortedData = data.sort((a, b) => a.sort_order - b.sort_order);

                    // 가장 큰 sort_order 값 찾기
                    const maxSortOrder = sortedData[sortedData.length - 1].sort_order;

                    // 0부터 maxSortOrder까지의 배열 생성

                    let currentDataIndex = 0;

                    // 0부터 maxSortOrder까지 순회하면서 데이터 또는 빈 행 추가
                    for (let i = 0; i <= maxSortOrder; i++) {
                        if (
                            currentDataIndex < sortedData.length &&
                            sortedData[currentDataIndex].sort_order === i
                        ) {
                            // sort_order가 일치하는 데이터가 있으면 추가
                            const row = sortedData[currentDataIndex];
                            rows.push([
                                row.memo1 || '',
                                row.memo2 || '',
                                row.memo3 || '',
                                row.store_name || '',
                                row.product_name || '',
                                row.product_url || '',
                                row.keyword || '',
                                row.product_id || '',
                                row.product_mid || '',
                            ]);
                            currentDataIndex++;
                        } else {
                            // sort_order가 없는 경우 빈 행 추가
                            rows.push(Array(9).fill(''));
                        }
                    }
                }
                const emptyRowsNeeded = Math.max(0, cap - rows.length);
                const emptyRows = Array.from({ length: emptyRowsNeeded }, () => Array(9).fill(''));

                const tableRows = [...rows, ...emptyRows];
                setTableData(tableRows);
                setLoadingMessage('');

                // 순위 데이터 가져오기 (baseRows 전달로 새로고침 시에도 올바르게 적용)
                const rankResults = await fetchRankResults();
                if (rankResults && rankResults.length > 0) {
                    applyResultToTable(rankResults, tableRows);
                }
            } else {
                setLoadingMessage('데이터 로드에 실패했습니다.');
            }
        } catch (err) {
            console.error('데이터 로드 에러:', err);
            setLoadingMessage('데이터 로드 중 오류가 발생했습니다.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInitialData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // afterOnCellContextMenu를 prop으로 등록하면 re-render마다 참조가 바뀌어 hook이 해제됨
    // → addHook으로 마운트 시 한 번만 등록
    useEffect(() => {
        const hot = hotRef.current?.hotInstance;
        if (!hot) return;
        const handler = (_event, coords) => {
            rightClickedRowRef.current = coords.row;
            rightClickedColRef.current = coords.col;
        };
        hot.addHook('afterOnCellContextMenu', handler);
        return () => {
            try {
                hot.removeHook('afterOnCellContextMenu', handler);
            } catch (_e) {
                /* instance already destroyed */
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleRefresh = async () => {
        if (isRefreshLoading || isSearchInProgress) return;
        try {
            setIsRefreshLoading(true);
            await fetchInitialData();
            message.success('데이터를 불러왔습니다.');
        } catch {
            message.error('새로고침에 실패했습니다.');
        } finally {
            setIsRefreshLoading(false);
        }
    };

    const saveRowsToServer = async changes => {
        console.log('🔄 저장 중:', changes);
        try {
            const response = await AuthService.authAxios.post(`/product/rank/tracking/rows`, {
                changes,
            });

            console.log('✅ 저장 완료:', response.data);
        } catch (error) {
            console.error('❌ 저장 실패:', error);
            const msg = error?.response?.data?.message || '저장에 실패했습니다.';
            message.warning(msg);
        }
    };

    const _deleteRankLogsFromServer = async sortOrders => {
        try {
            await AuthService.authAxios.post(`/product/rank/tracking/delete-rank-logs`, {
                sortOrders,
            });
        } catch (err) {
            console.error('❌ 순위 기록 삭제 실패:', err);
            message.error('순위 기록 삭제에 실패했습니다.');
        }
    };

    const deleteRankColumnFromServer = async requestIds => {
        try {
            await AuthService.authAxios.post(`/product/rank/tracking/delete-column`, {
                request_ids: requestIds,
            });
        } catch (err) {
            console.error('❌ 날짜 열 삭제 실패:', err);
            message.error('날짜 열 삭제에 실패했습니다.');
        }
    };

    const deleteRowsFromServer = async (sortOrders, isAllDelete = false) => {
        try {
            const response = await AuthService.authAxios.post(
                `/product/rank/tracking/delete`,
                { sortOrders, isAllDelete } // isAllDelete 플래그 추가
            );
            console.log('🗑️ 삭제 완료:', response.data);
        } catch (err) {
            console.error('❌ 삭제 실패:', err);
        }
    };

    const handleMidSearch = async () => {
        if (isSearchInProgress || trackingMidSearchLockRef.current) return;
        const currentData = [...tableData];
        // 사용 가능 행 수(maxRows)까지만 조회 (1번~maxRows번)
        const dataSlice = currentData.slice(0, maxRows);
        const lines = [];
        for (let index = 0; index < dataSlice.length; index++) {
            const row = dataSlice[index];
            const keyword = (row[6] ?? '').toString().trim();
            const productId = (row[7] ?? '').toString().trim();
            if (keyword && productId) {
                lines.push(`${keyword}\t${productId}\t${row[8] ?? ''}\t${index}`);
            }
        }

        if (lines.length === 0) {
            message.warning(
                `순위 조회 키워드와 단일 MID를 모두 입력한 행이 1~${maxRows}번 사이에 1개 이상 필요합니다.`
            );
            return;
        }

        trackingMidSearchLockRef.current = true;
        try {
            setLoading(true);
            setIsSearchInProgress(true);
            setLoadingMessage('📡 조회 요청을 서버에 전송 중입니다...');
            const user_id = AuthService.getCurrentUserId();
            const response = await AuthService.authAxios.post(
                '/product/rank/tracking/proxy/process',
                { lines, user_id }
            );
            const requestId = response.data?.request_id;
            if (!requestId) {
                setLoadingMessage('❌ 응답 형식 오류입니다.');
                trackingMidSearchLockRef.current = false;
                setLoading(false);
                setIsSearchInProgress(false);
                return;
            }
            dispatchQuotaUpdated('findNaverRankTracking');
            localStorage.setItem('request_id', requestId);
            setLoading(false); // 오버레이만 닫음 (기존처럼)
            startPolling(requestId);
            applyRealTimeResultToTable(response.data, 'column');
        } catch (e) {
            const msg = e?.response?.data?.message || '서버 오류가 발생했습니다.';
            setLoadingMessage(`❌ ${msg}`);
            message.error(msg);
            trackingMidSearchLockRef.current = false;
            setLoading(false);
            setIsSearchInProgress(false);
        }
    };

    const fetchRankResults = async () => {
        try {
            const user_id = AuthService.getCurrentUserId();
            const response = await AuthService.authAxios.get(
                `/product/rank/tracking/rank/history?user_id=${user_id}`
            );
            if (response.data.success) {
                return response.data.data;
            }
            return null;
        } catch (error) {
            console.error('순위 결과 조회 실패:', error);
            return null;
        }
    };

    const applyResultToTable = (results, baseRows = null) => {
        const hotInstance = hotRef.current?.hotInstance;
        // baseRows가 있으면 사용(새로고침 시), 없으면 hotInstance에서 가져옴
        const currentData =
            baseRows && Array.isArray(baseRows)
                ? baseRows.map(row => (Array.isArray(row) ? [...row] : []))
                : (hotInstance?.getSourceData() ?? []);
        if (!currentData.length && !baseRows) return;

        const sessionData = results;
        if (!sessionData || sessionData.length === 0) return;

        // 항상 기본 9열부터 시작 (새로고침 시 중복 방지)
        let newIndex = 9;

        const savedWidths = loadSavedColWidths();
        let updatedHeaders = [...BASE_COL_HEADERS];
        let updatedColumns = [...BASE_COLUMNS];
        let updatedWidths = (savedWidths && savedWidths.slice(0, 9)) || [...DEFAULT_COL_WIDTHS];
        const updatedData = currentData.map(row => (Array.isArray(row) ? [...row] : []));

        const formatDateHeader = s => {
            if (!s || s.length < 10) return s;
            return s.substring(0, 8).replace(/-/g, '.') + s.substring(8);
        };
        sessionData.forEach((session, sessionIndex) => {
            const raw = session.started_at.substring(2, 16);
            const newHeader = `${formatDateHeader(raw)}|${session.request_id}`;
            const newColumn = { data: newIndex };

            // 열 정보 누적
            updatedHeaders.push(newHeader);
            updatedColumns.push(newColumn);
            updatedWidths.push(savedWidths?.[newIndex] ?? DEFAULT_DATE_COL_WIDTH);

            // sessionData는 최신순(왼쪽=최신) → 이전 날짜 = 오른쪽 열 = sessionData[i+1]
            const prevSession =
                sessionIndex < sessionData.length - 1 ? sessionData[sessionIndex + 1] : null;
            const getPrevRank = sortOrder => {
                if (!prevSession) return null;
                const item = prevSession.rank.find(r => r.sort_order === sortOrder);
                if (!item || item.rank == null || item.rank === 'norank') return null;
                return typeof item.rank === 'number' ? item.rank : parseRankNumber(item.rank);
            };

            // 행 위치에 값 삽입 (rank 또는 status, 전날 대비 등락 표시)
            session.rank.forEach(rankItem => {
                const rowIndex = rankItem.sort_order;
                if (rowIndex >= 0 && rowIndex < updatedData.length) {
                    let value = rankItem.rank != null ? rankItem.rank : rankItem.status || '';
                    if (value === 'norank') {
                        value = '확인 불가';
                    } else {
                        const prevRank = getPrevRank(rankItem.sort_order);
                        const currNum = typeof value === 'number' ? value : parseRankNumber(value);
                        const formatted = formatRankWithChange(currNum, prevRank);
                        if (formatted) value = formatted;
                    }
                    updatedData[rowIndex][newIndex] = value;
                }
            });

            newIndex += 1;
        });

        // 최종 적용 (한 번에)
        setColHeaders(updatedHeaders);
        setColumns(updatedColumns);
        setColWidths(updatedWidths);
        setTableData(updatedData);

        hotRef.current?.hotInstance?.render();
    };

    const applyRealTimeResultToTable = (results, type) => {
        const hotInstance = hotRef.current?.hotInstance;
        if (!hotInstance) return;

        const insertIndex = 9;

        if (type == 'column') {
            //const newColumnDataIndex = columns.length;

            // 🔧 열 수가 정확히 맞는 새로운 행 생성
            const expectedColumnCount = columns.length + 1; // 새 열 포함
            const loadingSpinner = '<div class="loading-spinner"></div>';
            const updatedData = tableData.map((row, rowIndex) => {
                const newRow = [...row];
                const keyword = (row[6] ?? '').toString().trim();
                const productId = (row[7] ?? '').toString().trim();
                // 서버/프록시는 maxRows(1~N행)만 조회함. 그 아래 행에는 스피너를 넣지 않음(영구 로딩 방지)
                const inQuotaRow = rowIndex < maxRows;
                const cellValue = inQuotaRow && keyword && productId ? loadingSpinner : '';
                newRow.splice(insertIndex, 0, cellValue); // 중간 삽입
                while (newRow.length < expectedColumnCount) {
                    newRow.push('');
                }
                return newRow;
            });

            const formatDateHeader = s => {
                if (!s || s.length < 10) return s;
                return s.substring(0, 8).replace(/-/g, '.') + s.substring(8);
            };
            const raw = results.startDate.substring(2, 16);
            const updatedHeaders = [
                ...colHeaders.slice(0, insertIndex),
                `${formatDateHeader(raw)}|${results.request_id}`,
                ...colHeaders.slice(insertIndex),
            ];

            // columns
            const updatedColumns = [
                ...columns.slice(0, insertIndex),
                { data: 9 },
                ...columns.slice(insertIndex).map(col => ({
                    ...col,
                    data: typeof col.data === 'number' ? col.data + 1 : col.data,
                })),
            ];

            const savedWidths = loadSavedColWidths();
            const newColWidth = savedWidths?.[insertIndex] ?? DEFAULT_DATE_COL_WIDTH;
            const updatedWidths = [
                ...colWidths.slice(0, insertIndex),
                newColWidth,
                ...colWidths.slice(insertIndex),
            ];

            setColHeaders(updatedHeaders);
            setColumns(updatedColumns);
            setColWidths(updatedWidths);
            setTableData(updatedData);
        } else if (type == 'rank') {
            const currentData = hotInstance.getSourceData();
            const updatedData = currentData.map(row => [...row]);
            const prevColIndex = 10; // 새 열(9) 왼쪽의 이전 날짜 열

            results.forEach(result => {
                const rowIndex = result.sort_order;
                let value = result.rank;
                if (value != null && value !== 'norank') {
                    const prevCell = updatedData[rowIndex]?.[prevColIndex];
                    const prevRank = parseRankNumber(prevCell);
                    const currNum = typeof value === 'number' ? value : parseRankNumber(value);
                    const formatted = formatRankWithChange(currNum, prevRank);
                    if (formatted) value = formatted;
                } else if (value === 'norank') {
                    value = '확인 불가';
                }
                updatedData[rowIndex][insertIndex] = value;
            });

            for (let r = maxRows; r < updatedData.length; r++) {
                const v = updatedData[r]?.[insertIndex];
                if (typeof v === 'string' && v.includes('loading-spinner')) {
                    updatedData[r][insertIndex] = '';
                }
            }

            setTableData(updatedData);
        }
    };

    const startPolling = request_id => {
        if (pollingRef.current) clearInterval(pollingRef.current);

        setLoadingMessage('🔄 서버에서 결과를 조회 중입니다... 화면을 나가면 조회가 취소됩니다.');

        pollingRef.current = setInterval(async () => {
            try {
                const res = await AuthService.authAxios.get(
                    `/product/rank/tracking/proxy/status/${request_id}`
                );
                const { status, result } = res.data;
                if (status === 'done') {
                    clearInterval(pollingRef.current);
                    pollingRef.current = null;
                    localStorage.removeItem('request_id');
                    applyRealTimeResultToTable(result, 'rank');
                    setLoadingMessage('✅ 순위 조회가 완료되었습니다.');
                    trackingMidSearchLockRef.current = false;
                    setIsSearchInProgress(false); // 버튼만 활성화
                }
            } catch (e) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
                localStorage.removeItem('request_id');
                trackingMidSearchLockRef.current = false;
                setIsSearchInProgress(false);
            }
        }, 2000);
    };

    return (
        <ProtectedMenuPage menuKey="findNaverRankTracking">
            <div className="find-naver-rank-tracking-page">
                <div className="page-header-container no-header-border">
                    <Card className="control-card" bodyStyle={{ padding: '8px 16px' }}>
                        <Row justify="space-between" align="middle">
                            <Col>
                                <Space size="small">
                                    <Popover
                                        content={
                                            <div className="col-visibility-popover">
                                                <div
                                                    style={{
                                                        marginBottom: 8,
                                                        fontSize: 12,
                                                        color: '#64748b',
                                                    }}
                                                >
                                                    행 표시 (ON=노출, OFF=숨김)
                                                </div>
                                                {COL_VISIBILITY_OPTIONS.map(opt => (
                                                    <div
                                                        key={opt.key}
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            marginBottom: 6,
                                                            minWidth: 140,
                                                        }}
                                                    >
                                                        <span style={{ fontSize: 13 }}>
                                                            {opt.label}
                                                        </span>
                                                        <Switch
                                                            size="small"
                                                            checked={colVisibility[opt.key]}
                                                            onChange={checked =>
                                                                setColVisibility(prev => ({
                                                                    ...prev,
                                                                    [opt.key]: checked,
                                                                }))
                                                            }
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        }
                                        trigger="click"
                                        placement="bottomLeft"
                                    >
                                        <Button
                                            type="text"
                                            size="small"
                                            icon={<EyeOutlined />}
                                            className="page-action-btn"
                                        >
                                            행 표시
                                        </Button>
                                    </Popover>
                                    <Text type="secondary">사용(조회) 가능 행 수: </Text>
                                    <Text strong>{maxRows}</Text>
                                </Space>
                            </Col>
                            <Col>
                                <Space size="small" className="right-actions-space">
                                    <Button
                                        type="primary"
                                        icon={<SearchOutlined />}
                                        onClick={handleMidSearch}
                                        loading={isSearchInProgress}
                                        style={{ minWidth: '120px' }}
                                    >
                                        실시간 순위 조회 시작
                                    </Button>
                                    <Button
                                        className="page-action-btn"
                                        icon={<ReloadOutlined />}
                                        onClick={handleRefresh}
                                        loading={isRefreshLoading}
                                        disabled={isSearchInProgress}
                                        title="DB에서 데이터 불러와 갱신"
                                    >
                                        새로고침
                                    </Button>
                                    <Button
                                        type="ghost"
                                        className="excel-button"
                                        icon={<DownloadOutlined />}
                                        onClick={handleExportExcel}
                                        title="현재 테이블 전체를 엑셀로 저장"
                                    />
                                </Space>
                            </Col>
                        </Row>
                    </Card>
                </div>

                <div className="page-content-inner">
                    {/* 3. Main Data/Table Section */}
                    <div className="naver-rank-tracking-table-wrapper">
                        {loading && (
                            <div className="naver-rank-tracking-loading-overlay">
                                <Spin size="large" tip={loadingMessage || '로딩 중...'} />
                            </div>
                        )}
                        <div className="naver-rank-tracking-table">
                            <HotTable
                                ref={hotRef}
                                data={tableData}
                                colHeaders={visibleColHeaders.map(header =>
                                    header.includes('|') ? header.split('|')[0] : header
                                )}
                                columns={visibleColumns}
                                colWidths={visibleColWidths}
                                manualColumnResize={true}
                                rowHeaders={true}
                                autoWrapRow={true}
                                autoWrapCol={true}
                                selectionMode="row"
                                contextMenu={{
                                    items: {
                                        remove_rank_logs: {
                                            name: '선택 행 삭제',
                                            hidden: () => {
                                                const visualRow = rightClickedRowRef.current;
                                                return visualRow == null || visualRow < 0;
                                            },
                                            callback: () => {
                                                const hot = hotRef.current?.hotInstance;
                                                if (!hot) return;
                                                const range = hot.getSelectedRangeLast();
                                                const clickedRow = rightClickedRowRef.current;
                                                let fromRow, toRow;
                                                if (range && range.from.row >= 0) {
                                                    fromRow = Math.min(
                                                        range.from.row,
                                                        range.to.row
                                                    );
                                                    toRow = Math.max(range.from.row, range.to.row);
                                                } else if (clickedRow != null && clickedRow >= 0) {
                                                    fromRow = toRow = clickedRow;
                                                } else {
                                                    return;
                                                }
                                                if (fromRow < 0) fromRow = 0;
                                                const count = toRow - fromRow + 1;
                                                Modal.confirm({
                                                    title: '행 삭제',
                                                    content: `${count}개 행을 삭제합니다.\n키워드, MID, 순위 기록이 모두 삭제됩니다.\n삭제 후 복구할 수 없습니다.`,
                                                    okText: '삭제',
                                                    okType: 'danger',
                                                    cancelText: '취소',
                                                    onOk: () => {
                                                        skipRemoveRowConfirmRef.current = true;
                                                        hot.alter('remove_row', fromRow, count);
                                                    },
                                                });
                                            },
                                        },
                                        remove_rank_column: {
                                            name: '선택 열 삭제',
                                            hidden: () => {
                                                const visualCol = rightClickedColRef.current;
                                                if (visualCol == null) return true;
                                                const dataCol =
                                                    visibleColumns[visualCol]?.data ?? visualCol;
                                                return dataCol < 9;
                                            },
                                            callback: () => {
                                                const hot = hotRef.current?.hotInstance;
                                                if (!hot) return;
                                                const range = hot.getSelectedRangeLast();
                                                const clickedCol = rightClickedColRef.current;
                                                let fromCol, toCol;
                                                if (range) {
                                                    fromCol = Math.min(
                                                        range.from.col,
                                                        range.to.col
                                                    );
                                                    toCol = Math.max(range.from.col, range.to.col);
                                                } else if (clickedCol != null) {
                                                    fromCol = toCol = clickedCol;
                                                } else {
                                                    return;
                                                }
                                                const dataCols = [];
                                                const requestIds = [];
                                                for (let vc = fromCol; vc <= toCol; vc++) {
                                                    const dc = visibleColumns[vc]?.data ?? vc;
                                                    if (dc < 9) continue;
                                                    const headerText = colHeaders[dc];
                                                    if (!headerText?.includes('|')) continue;
                                                    const [, reqId] = headerText.split('|');
                                                    dataCols.push(dc);
                                                    requestIds.push(reqId);
                                                }
                                                if (requestIds.length === 0) return;
                                                Modal.confirm({
                                                    title: '날짜 열 삭제',
                                                    content: `${requestIds.length}개 날짜의 순위 기록을 모두 삭제합니다.\n삭제 후 복구할 수 없습니다.`,
                                                    okText: '삭제',
                                                    okType: 'danger',
                                                    cancelText: '취소',
                                                    onOk: async () => {
                                                        await deleteRankColumnFromServer(
                                                            requestIds
                                                        );
                                                        const dataColSet = new Set(dataCols);
                                                        setColHeaders(prev =>
                                                            prev.filter(
                                                                (_, i) => !dataColSet.has(i)
                                                            )
                                                        );
                                                        setColumns(prev =>
                                                            prev
                                                                .filter(
                                                                    (_, i) => !dataColSet.has(i)
                                                                )
                                                                .map((_, i) => ({ data: i }))
                                                        );
                                                        setColWidths(prev =>
                                                            prev.filter(
                                                                (_, i) => !dataColSet.has(i)
                                                            )
                                                        );
                                                        setTableData(prev =>
                                                            prev.map(row =>
                                                                row.filter(
                                                                    (_, i) => !dataColSet.has(i)
                                                                )
                                                            )
                                                        );
                                                    },
                                                });
                                            },
                                        },
                                        copy: { name: '복사' },
                                        cut: { name: '잘라내기' },
                                    },
                                }}
                                stretchH="all"
                                height="100%"
                                width="100%"
                                afterColumnResize={(newSize, column) => {
                                    const dataCol = visibleColumns[column]?.data ?? column;
                                    setColWidths(prev => {
                                        const next = [...prev];
                                        next[dataCol] = newSize;
                                        saveColWidths(next);
                                        return next;
                                    });
                                }}
                                cells={(row, col) => {
                                    const dataCol = visibleColumns[col]?.data ?? col;
                                    // maxRows는 순위 조회·행 추가 한도용. DB에 더 많은 행이 있어도 편집 가능.
                                    if (dataCol >= 6 && dataCol <= 8) {
                                        const classes = ['required-column'];
                                        if (dataCol === 7 || dataCol === 8)
                                            classes.push('mid-column');
                                        return { className: classes.join(' ') };
                                    }
                                    if (dataCol >= 9) {
                                        const cellVal = tableData[row]?.[dataCol];
                                        const str = String(cellVal || '');
                                        const classes = ['rank-column'];
                                        if (str.includes('▲')) classes.push('rank-up');
                                        else if (str.includes('▼')) classes.push('rank-down');
                                        return {
                                            readOnly: true,
                                            className: classes.join(' '),
                                            renderer: rankCellRenderer,
                                        };
                                    }
                                    return {};
                                }}
                                beforeRemoveCol={index => {
                                    const dataCol = visibleColumns[index]?.data ?? index;
                                    if (dataCol < 9) {
                                        message.warning('기본 열은 삭제할 수 없습니다.');
                                        return false;
                                    }
                                    return true;
                                }}
                                beforeCut={(data, coords) => {
                                    const buffer = [];
                                    const coord = coords[0];
                                    for (let row = coord.startRow; row <= coord.endRow; row++) {
                                        for (
                                            let vCol = coord.startCol;
                                            vCol <= coord.endCol;
                                            vCol++
                                        ) {
                                            const dataCol = visibleColumns[vCol]?.data ?? vCol;
                                            const oldValue = tableData?.[row]?.[dataCol] ?? '';
                                            buffer.push({
                                                row,
                                                col: dataCol,
                                                oldValue,
                                            });
                                        }
                                    }
                                    cutBufferRef.current = buffer;
                                }}
                                afterChange={(changes, source) => {
                                    if (
                                        !changes ||
                                        !['edit', 'CopyPaste.paste', 'UndoRedo.undo'].includes(
                                            source
                                        )
                                    )
                                        return;

                                    const isAllRowsEmpty = tableData.every(rowData =>
                                        rowData.every(cell => {
                                            if (cell == null) return true;
                                            const strCell = String(cell);
                                            return strCell.trim() === '';
                                        })
                                    );

                                    if (isAllRowsEmpty) {
                                        const emptyRowIndex = changes[0][0];
                                        deleteRowsFromServer([emptyRowIndex], true);
                                        return;
                                    }

                                    // afterChange의 두 번째 값은 시각적 열이 아니라 데이터 열(prop)입니다.
                                    // (core setDataAtCell → colToProp). visibleColumns로 다시 매핑하면
                                    // 열 숨김 시 엇갈려 잘못된 컬럼으로 저장됩니다.
                                    const formattedChanges = changes.map(
                                        ([row, prop, oldVal, newVal]) => ({
                                            row,
                                            col: prop,
                                            oldValue: oldVal,
                                            newValue: newVal,
                                        })
                                    );

                                    const clearedCells = cutBufferRef.current
                                        .filter(({ row, col }) => {
                                            const currentVal = tableData?.[row]?.[col] ?? '';
                                            return currentVal === '';
                                        })
                                        .map(({ row, col, oldValue }) => ({
                                            row,
                                            col,
                                            oldValue,
                                            newValue: '',
                                        }));

                                    cutBufferRef.current = [];

                                    const totalChanges = [...formattedChanges, ...clearedCells];
                                    saveRowsToServer(totalChanges);
                                }}
                                afterRemoveRow={(index, amount, physicalRows) => {
                                    const deletedSortOrders = physicalRows;
                                    deleteRowsFromServer(deletedSortOrders);
                                    setDbRowCount(prev => prev - physicalRows.length);

                                    setTableData(prevData => {
                                        const emptyRowsNeeded = Math.max(
                                            0,
                                            maxRows - prevData.length
                                        );
                                        const emptyRows = Array.from(
                                            { length: emptyRowsNeeded },
                                            () => Array(9).fill('')
                                        );

                                        return [...prevData, ...emptyRows];
                                    });
                                }}
                                beforeRemoveRow={(index, amount) => {
                                    if (skipRemoveRowConfirmRef.current) {
                                        skipRemoveRowConfirmRef.current = false;
                                        return true;
                                    }
                                    Modal.confirm({
                                        title: '행 삭제',
                                        content:
                                            '삭제된 행은 복원할 수 없으며, 조회된 순위 데이터도 다시 확인할 수 없습니다. 삭제할까요?',
                                        okText: '삭제',
                                        okType: 'danger',
                                        cancelText: '취소',
                                        onOk: () => {
                                            const hot = hotRef.current?.hotInstance;
                                            if (!hot) return;
                                            skipRemoveRowConfirmRef.current = true;
                                            hot.alter('remove_row', index, amount);
                                        },
                                    });
                                    return false;
                                }}
                                beforeCreateRow={(index, amount, source) => {
                                    if (dbRowCount + amount > maxRows) {
                                        message.warning(
                                            `최대 ${maxRows}개 행까지만 생성할 수 있습니다.`
                                        );
                                        return false;
                                    }

                                    if (
                                        source === 'ContextMenu.rowAbove' ||
                                        source === 'ContextMenu.rowBelow'
                                    ) {
                                        const position = source.includes('rowAbove')
                                            ? 'rowAbove'
                                            : 'rowBelow';

                                        AuthService.authAxios
                                            .post('/product/rank/tracking/insert-row', {
                                                position,
                                                index,
                                            })
                                            .then(response => {
                                                if (response.data.success) {
                                                    setDbRowCount(prev => prev + amount);
                                                    return true;
                                                }
                                                return false;
                                            })
                                            .catch(err => {
                                                const msg =
                                                    err?.response?.data?.message ||
                                                    '행 추가에 실패했습니다.';
                                                message.warning(msg);
                                                return false;
                                            });
                                    }
                                    return true;
                                }}
                                licenseKey="non-commercial-and-evaluation"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <KakaoFloatButton />
        </ProtectedMenuPage>
    );
};

export { FindNaverRankTracking };
